<script>
import axios from "axios";
import { mapGetters } from "vuex";
import Swal from "sweetalert2";
import AuthenticatedProfileImage from "@/components/base/AuthenticatedProfileImage.vue";
import { canCompleteOutstandingOLevel } from "@/utils/applicationOLevelUtils";

export default {
  name: "AppResources",
  components: { AuthenticatedProfileImage },
  data: () => ({
    applicationRecord: null,
    uploadedDocuments: [],
    replacementRequests: [],
    replacementFiles: {},
    replacementUploadingId: null,
    loading: true,
    error: "",
    systemEnabled: false,
    documentTiles: [
      { name: "ID Card", icon: "bi bi-person-vcard", future: true },
      {
        key: "applicationForm",
        name: "Application Form",
        icon: "bi bi-file-earmark-richtext",
      },
      { name: "Course Form", icon: "bi bi-file-earmark-text", future: true },
      { name: "SIWES Form", icon: "bi bi-file-earmark-text", future: true },
      { name: "Transcript", icon: "bi bi-file-text", future: true },
      {
        name: "Pay Receipts",
        icon: "bi bi-receipt",
        route: "finance",
      },
      { name: "Exam Docket", icon: "bi bi-card-text", future: true },
      { name: "Hostel Slip", icon: "bi bi-file-earmark-text", future: true },
    ],
    contacts: [
      { title: "Rector", email: "rector@firstcitypoly.edu.ng", phone: "" },
      { title: "Bursary Unit", email: "", phone: "" },
      {
        title: "Registry Unit",
        email: "registrar@firstcitypoly.edu.ng",
        phone: "",
      },
      {
        title: "ICT Department",
        email: "directorict@firstcitypoly.edu.ng",
        phone: "",
      },
      { title: "Student Affairs Office", email: "", phone: "" },
      {
        title: "Admissions Office",
        email: "admission@firstcitypoly.edu.ng",
        phone: "+234 706 337 7322",
      },
      { title: "Counselling Unit", email: "", phone: "" },
      { title: "Library Services", email: "", phone: "" },
      { title: "Exam & Records Unit", email: "", phone: "" },
      { title: "Academic Planning", email: "", phone: "" },
      { title: "Security Office", email: "", phone: "" },
      { title: "Sports & Recreation", email: "", phone: "" },
      {
        title: "General Inquiries",
        email: "info@firstcitypolytechnic.edu.ng",
        phone: "",
      },
    ],
  }),
  computed: {
    ...mapGetters(["getUser", "getApplication", "getStudent"]),
    application() {
      return this.applicationRecord || this.getApplication || {};
    },
    offering() {
      return (
        this.application?.programOfferingId ||
        this.getStudent?.currentEnrollmentId?.programOfferingId ||
        this.getStudent?.initialProgramOfferingId ||
        {}
      );
    },
    canCompleteOLevel() {
      return canCompleteOutstandingOLevel(this.application);
    },
    openReplacementRequests() {
      return this.replacementRequests.filter(
        (request) => request.status === "open"
      );
    },
    canDownloadApplication() {
      return (
        this.application?.status &&
        this.application.status !== "draft" &&
        this.application?.oLevelResultStatus !== "awaiting_result"
      );
    },
    resolvedDocumentTiles() {
      return this.documentTiles.map((tile) => {
        if (tile.key !== "applicationForm") {
          return { ...tile, enabled: Boolean(tile.route) };
        }
        if (this.canCompleteOLevel) {
          return {
            ...tile,
            name: "Complete Application Form",
            enabled: true,
            route: "applicationOLevelCompletion",
            actionIcon: "bi bi-pencil-square",
          };
        }
        return {
          ...tile,
          enabled: this.canDownloadApplication,
          action: this.canDownloadApplication ? "download-application" : null,
          actionIcon: "bi bi-download",
        };
      });
    },
    fullName() {
      return [
        this.getUser?.firstName,
        this.getUser?.otherName,
        this.getUser?.lastName,
      ]
        .filter(Boolean)
        .join(" ");
    },
    profileData() {
      return [
        { label: "Name", value: this.fullName },
        {
          label: "Date of Birth",
          value: this.formatDate(this.getUser?.dateOfBirth),
        },
        { label: "Gender", value: this.application?.gender },
        { label: "Nationality", value: this.application?.nationality },
        { label: "State of Origin", value: this.application?.stateOfOrigin },
        { label: "Place of Birth", value: this.application?.placeOfBirth },
        { label: "Marital Status", value: this.application?.maritalStatus },
        { label: "Next of Kin", value: this.application?.nextOfKin?.name },
        {
          label: "Next of Kin Phone",
          value: this.application?.nextOfKin?.phone,
        },
        { label: "Blood Group", value: "Not captured" },
        { label: "Medical Conditions", value: "Not captured" },
      ];
    },
  },
  async mounted() {
    await this.loadResources();
  },
  methods: {
    formatValue(value, fallback = "N/A") {
      return value === null || value === undefined || value === ""
        ? fallback
        : value;
    },
    formatDate(value) {
      if (!value) return "N/A";
      const date = new Date(value);
      return Number.isNaN(date.getTime())
        ? "N/A"
        : date.toLocaleDateString("en-GB");
    },
    formatAddress(address) {
      if (!address) return "N/A";
      if (typeof address === "string") return address;
      return (
        [
          address.line1,
          address.line2,
          address.city,
          address.state,
          address.country,
        ]
          .filter(Boolean)
          .join(", ") || "N/A"
      );
    },
    async loadResources() {
      this.loading = true;
      this.error = "";
      try {
        const [applicationResponse, documentsResponse, replacementsResponse] =
          await Promise.all([
            axios.get(`${process.env.VUE_APP_API_BASE_URL}/applications/me`),
            axios.get(
              `${process.env.VUE_APP_API_BASE_URL}/file-upload/documents`
            ),
            axios.get(
              `${process.env.VUE_APP_API_BASE_URL}/file-upload/replacement-requests`
            ),
          ]);
        this.applicationRecord = applicationResponse.data?.data || null;
        this.uploadedDocuments = documentsResponse.data?.data || [];
        this.replacementRequests = replacementsResponse.data?.data || [];
      } catch (error) {
        this.error =
          error.response?.data?.message || "Unable to load portal resources.";
      } finally {
        this.loading = false;
      }
    },
    replacementLabel(request) {
      const labels = {
        profile_image: "Profile Image",
        o_level_result: "O-Level Result",
        jamb_result: "JAMB Result",
        birth_certificate: "Birth Certificate",
      };
      return `${labels[request.documentType] || request.documentType}${
        request.sittingNumber ? ` · Sitting ${request.sittingNumber}` : ""
      }`;
    },
    setReplacementFile(requestId, event) {
      this.replacementFiles = {
        ...this.replacementFiles,
        [requestId]: event.target.files?.[0] || null,
      };
    },
    async uploadRequestedReplacement(request) {
      const file = this.replacementFiles[request._id];
      if (!file) {
        this.error = "Select the requested replacement file first.";
        return;
      }
      this.error = "";
      this.replacementUploadingId = request._id;
      try {
        const form = new FormData();
        form.append("file", file);
        form.append("fileType", request.documentType);
        if (request.sittingNumber)
          form.append("sittingNumber", request.sittingNumber);
        form.append("replacementRequestId", request._id);
        await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/file-upload/documents`,
          form,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        await this.loadResources();
        await Swal.fire(
          "Uploaded",
          "Your replacement document was sent to Registry for review.",
          "success"
        );
      } catch (error) {
        this.error =
          error.response?.data?.message ||
          "Unable to upload the replacement document.";
      } finally {
        this.replacementUploadingId = null;
      }
    },
    async handleDocumentAction(tile) {
      if (!tile.enabled) return;
      if (tile.route) {
        await this.$router.push({ name: tile.route });
        return;
      }
      if (tile.action === "download-application") {
        await this.downloadApplicationForm();
      }
    },
    examinationRows() {
      const examinations = this.application?.examinations || [];
      if (!examinations.length)
        return [["No examination details available", "", "", "", ""]];
      return examinations.map((exam, index) => [
        `Sitting ${index + 1}`,
        exam.examType || "N/A",
        exam.examYear || "N/A",
        exam.examNumber || "N/A",
        (exam.subjects || [])
          .map((subject) => `${subject.name}: ${subject.grade}`)
          .join(", ") || "N/A",
      ]);
    },
    documentRows() {
      const labels = {
        profile_image: "Profile Image",
        o_level_result: "O-Level Result",
        jamb_result: "JAMB Result",
        birth_certificate: "Birth Certificate",
      };
      const rows = this.uploadedDocuments.map((document) => [
        labels[document.documentType] || document.documentType,
        document.sittingNumber ? `Sitting ${document.sittingNumber}` : "—",
        document.status || "uploaded",
      ]);
      return rows.length ? rows : [["No uploaded documents", "—", "—"]];
    },
    async downloadApplicationForm() {
      const jsPDF = (await import("jspdf")).default;
      await import("jspdf-autotable");
      const doc = new jsPDF();
      const application = this.application;
      const identifier =
        application.applicationNumber ||
        this.getStudent?.matriculationNumber ||
        "application";

      doc.setFontSize(17);
      doc.text("First City Polytechnic", 14, 17);
      doc.setFontSize(13);
      doc.text("Application Form", 14, 25);
      doc.setFontSize(9);
      doc.text(`Generated: ${new Date().toLocaleString("en-GB")}`, 14, 31);
      doc.autoTable({
        startY: 37,
        head: [["Personal Information", "Value"]],
        body: [
          ["Application Number", identifier],
          ["Name", this.fullName || "N/A"],
          ["Email", this.getUser?.email || "N/A"],
          ["Phone", this.getUser?.phone || "N/A"],
          ["Date of Birth", this.formatDate(this.getUser?.dateOfBirth)],
          ["Gender", this.formatValue(application.gender)],
          ["Address", this.formatAddress(application.permanentAddress)],
          ["Nationality", this.formatValue(application.nationality)],
          ["State of Origin", this.formatValue(application.stateOfOrigin)],
          ["LGA", this.formatValue(application.lga)],
          ["Guardian", this.formatValue(application.guardian?.name)],
          ["Guardian Phone", this.formatValue(application.guardian?.phone)],
          ["Next of Kin", this.formatValue(application.nextOfKin?.name)],
          ["Next of Kin Phone", this.formatValue(application.nextOfKin?.phone)],
        ],
        headStyles: { fillColor: [33, 37, 41] },
      });
      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 7,
        head: [["Programme Information", "Value"]],
        body: [
          ["Session", application.entrySessionId?.sessionYear || "N/A"],
          ["Programme", this.offering?.program?.name || "N/A"],
          ["Department", this.offering?.department?.name || "N/A"],
          ["School", this.offering?.department?.school?.name || "N/A"],
          ["Programme Type", this.offering?.structure?.type?.type || "N/A"],
          ["Mode", this.offering?.structure?.mode?.description || "N/A"],
        ],
        headStyles: { fillColor: [33, 37, 41] },
      });
      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 7,
        head: [["Sitting", "Exam", "Year", "Number", "Subjects"]],
        body: this.examinationRows(),
        styles: { fontSize: 8 },
        headStyles: { fillColor: [33, 37, 41] },
      });
      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 7,
        head: [["Document", "Detail", "Status"]],
        body: this.documentRows(),
        headStyles: { fillColor: [33, 37, 41] },
      });
      doc.save(`Application_Form_${identifier}.pdf`);
    },
  },
};
</script>

<template>
  <div class="container-fluid p-4 text-start">
    <div class="d-flex justify-content-between align-items-center">
      <h4 class="fw-bold mb-0">Resources</h4>
      <button
        class="btn btn-outline-primary btn-sm"
        :disabled="loading"
        @click="loadResources"
      >
        <i class="bi bi-arrow-clockwise"></i> Refresh
      </button>
    </div>
    <hr />
    <div v-if="error" class="alert alert-danger">{{ error }}</div>

    <section
      v-if="openReplacementRequests.length"
      class="card border-warning shadow-sm mt-3"
      :class="{ 'replacement-highlight': $route.query.replacement }"
    >
      <div class="card-header bg-warning-subtle">
        <h6 class="fw-bold mb-1">
          <i class="bi bi-exclamation-circle me-2"></i>Document correction
          required
        </h6>
        <small
          >Registry has reopened only the document slots listed below. Your
          other submitted documents remain locked.</small
        >
      </div>
      <div class="card-body">
        <div
          v-for="request in openReplacementRequests"
          :key="request._id"
          class="border rounded p-3 mb-3"
          :class="{
            'border-primary border-2': $route.query.replacement === request._id,
          }"
        >
          <div class="d-flex justify-content-between gap-3 flex-wrap mb-2">
            <div>
              <strong>{{ replacementLabel(request) }}</strong>
              <p class="text-muted mb-0">{{ request.reason }}</p>
            </div>
            <span class="badge text-bg-warning align-self-start"
              >Replacement requested</span
            >
          </div>
          <div class="row g-2 align-items-end">
            <div class="col-md-8">
              <label class="form-label small">Replacement file</label
              ><input
                type="file"
                class="form-control"
                :accept="
                  request.documentType === 'profile_image'
                    ? 'image/jpeg,image/png'
                    : 'application/pdf'
                "
                @change="setReplacementFile(request._id, $event)"
              />
            </div>
            <div class="col-md-4">
              <button
                class="btn btn-primary w-100"
                type="button"
                :disabled="replacementUploadingId === request._id"
                @click="uploadRequestedReplacement(request)"
              >
                <span
                  v-if="replacementUploadingId === request._id"
                  class="spinner-border spinner-border-sm me-1"
                ></span
                >Upload replacement
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div class="row g-4 mt-3 mb-5">
      <div class="col-md-6">
        <h6>Your Documents</h6>
        <div class="row g-3">
          <div
            v-for="tile in resolvedDocumentTiles"
            :key="tile.name"
            class="col-6 col-md-3"
          >
            <div class="d-flex flex-column text-center h-100">
              <button
                type="button"
                class="btn d-grid px-3 document-button"
                :class="tile.enabled ? 'btn-outline-primary' : ''"
                :disabled="!tile.enabled"
                :title="tile.future ? 'Planned for a future sprint' : tile.name"
                @click="handleDocumentAction(tile)"
              >
                <i :class="tile.icon" class="fs-1"></i>
                <i
                  :class="tile.actionIcon || 'bi bi-download'"
                  class="fs-5"
                ></i>
              </button>
              <span class="mt-2 small">{{ tile.name }}</span>
            </div>
          </div>
        </div>
      </div>

      <div id="map" class="col-md-6 scroll-target">
        <h6>Campus Map</h6>
        <div class="card border-0 shadow-sm overflow-hidden">
          <iframe
            title="First City Polytechnic campus map"
            src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d3958.9137717088365!2d3.326502!3d7.1359698!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x103a4dcf64927fb7%3A0xdb8aeb6daef45d65!2sFirst%20City%20Polytechnic%20Abeokuta!5e0!3m2!1sen!2sng!4v1741030966414!5m2!1sen!2sng"
            height="300"
            style="border: 0"
            allowfullscreen
            loading="lazy"
            referrerpolicy="no-referrer-when-downgrade"
          ></iframe>
        </div>
      </div>
    </div>

    <div class="row mb-5">
      <div class="col-12">
        <h6>Upcoming Events</h6>
        <div
          class="card border-0 shadow-sm p-4 position-relative overflow-hidden"
        >
          <div class="d-flex gap-4 flex-nowrap overflow-hidden">
            <div
              v-for="n in 5"
              :key="n"
              class="event-placeholder flex-shrink-0"
            >
              <div class="bg-secondary rounded-3" style="height: 180px"></div>
              <p class="placeholder-glow mt-3 mb-1">
                <span class="placeholder col-8"></span>
              </p>
              <p class="placeholder-glow">
                <span class="placeholder col-5"></span>
              </p>
            </div>
          </div>
          <div
            v-if="!systemEnabled"
            class="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-50"
          >
            <h5 class="text-white fw-bold">Not activated yet</h5>
          </div>
        </div>
      </div>
    </div>

    <div class="row g-4 mb-5">
      <div class="col-lg-8">
        <h6>Medical ID & Biodata</h6>
        <div class="card border-0 shadow-sm position-relative">
          <div class="card-body">
            <div class="row g-4">
              <div class="col-md-4 text-center">
                <AuthenticatedProfileImage
                  :size="130"
                  class="rounded-circle border border-2 border-primary mb-3"
                  alt="Profile image"
                />
                <h6 class="text-capitalize">{{ fullName }}</h6>
                <span class="small text-muted">
                  {{
                    getStudent?.matriculationNumber ||
                    application?.applicationNumber
                  }}
                </span>
              </div>
              <div class="col-md-8">
                <div
                  v-for="item in profileData"
                  :key="item.label"
                  class="row small mb-2"
                >
                  <div class="col-5 fw-bold text-secondary">
                    {{ item.label }}
                  </div>
                  <div class="col-7 text-capitalize">
                    {{ formatValue(item.value) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            class="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-50 rounded-3"
            style="cursor: not-allowed"
          >
            <div class="text-center text-white px-3">
              <h5 class="fw-bold mb-1">Medical records not activated yet</h5>
              <small>Your available biodata remains securely stored.</small>
            </div>
          </div>
        </div>
      </div>

      <div id="contacts" class="col-lg-4 scroll-target">
        <h6>Useful Contacts</h6>
        <div class="card border-0 shadow-sm">
          <div class="card-body overflow-auto contacts-list">
            <div
              v-for="contact in contacts"
              :key="contact.title"
              class="border-bottom pb-2 mb-2"
            >
              <div class="fw-bold small">{{ contact.title }}</div>
              <a
                v-if="contact.email"
                :href="`mailto:${contact.email}`"
                class="d-block small text-break"
              >
                {{ contact.email }}
              </a>
              <a
                v-if="contact.phone"
                :href="`tel:${contact.phone}`"
                class="d-block small"
              >
                {{ contact.phone }}
              </a>
              <span
                v-if="!contact.email && !contact.phone"
                class="small text-muted"
                >Contact details pending</span
              >
            </div>
          </div>
          <div class="card-footer bg-white d-flex gap-2">
            <button class="btn btn-outline-secondary btn-sm flex-fill" disabled>
              Enquiry
            </button>
            <button class="btn btn-outline-secondary btn-sm flex-fill" disabled>
              Complaint
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.document-button {
  min-height: 100px;
}

.event-placeholder {
  width: 220px;
}

.contacts-list {
  max-height: 300px;
}

.scroll-target {
  scroll-margin-top: 90px;
}
</style>
